import React from 'react';
import PropTypes from 'prop-types';
import styled, { injectGlobal } from 'styled-components';
import {
  SortableContainer, SortableElement, SortableHandle, arrayMove
} from 'react-sortable-hoc';
import * as _ from 'lodash';
import Widget from 'home/Widget';
import { INK_2, INK_3, LINE, BG } from 'common/styles/listUI';

/**
 * Devices insights bar. Presents the device breakdown widgets (reachability /
 * battery / wifi) as a top insights strip that matches the Assets page Insights
 * layout and drag behaviour, replacing the old right-side widget sidebar.
 * Toggled from the shared FilterBar's "Insights" button (collapsed by default).
 *
 * Reordering uses react-sortable-hoc with a grip handle — identical to the
 * Assets InsightsDrawer — so cards drag only from the grip (chart interactions
 * still work). The chosen order is persisted to localStorage.
 */

const WIDGET_KEYS = [
  'device_reachability_breakdown',
  'device_battery_level_breakdown',
  'device_wifi_strength_breakdown'
];

const ORDER_STORAGE_KEY = 'device_insights_order';

const Drawer = styled.div`
  background: ${BG};
  border-bottom: 1px solid ${LINE};
  padding: 12px 20px;
  overflow-x: auto;
  overflow-y: visible;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;

const Title = styled.span`
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${INK_2};
`;

const Hint = styled.span`
  font-size: 11px;
  color: ${INK_3};
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  align-items: stretch;
`;

const Card = styled.div`
  position: relative;
  background: white;
  border: 1px solid ${LINE};
  border-radius: 10px;
  padding: 12px 14px;
  flex-shrink: 0;
  user-select: none;
`;

const GripEl = styled.span`
  position: absolute;
  top: 6px;
  right: 7px;
  color: #d2d6cd;
  font-size: 11px;
  letter-spacing: -2px;
  cursor: grab;
  z-index: 1;

  &:hover { color: ${INK_3}; }
  &:active { cursor: grabbing; }
`;

// Drag handle: drag from the grip only, so chart interactions still fire.
const Grip = SortableHandle(() => <GripEl>⠿</GripEl>);

// While dragging, react-sortable-hoc clones the card into a floating helper.
// eslint-disable-next-line no-unused-expressions, chai-friendly/no-unused-expressions
injectGlobal`
  .device-insights-card-helper {
    z-index: 4000;
    cursor: grabbing !important;
    box-shadow: 0 14px 30px rgba(40, 44, 36, 0.22);
    border-radius: 10px;
    background: white;
    opacity: 0.98;
    transform: scale(1.03);
  }
`;

const SortableCard = SortableElement(({ metadata, chartFilters }) => (
  <Card>
    <Grip />
    <Widget filters={chartFilters} metadata={metadata} width="280px" height="200px" />
  </Card>
));

const SortableRow = SortableContainer(({ items, metadataByKey, chartFilters }) => (
  <Row>
    {items.map((key, index) => (
      <SortableCard
        key={key}
        index={index}
        metadata={metadataByKey[key]}
        chartFilters={chartFilters}
      />
    ))}
  </Row>
));

class DevicesInsights extends React.Component {
  constructor(props) {
    super(props);
    this.state = { order: this.loadOrder() };
  }

  loadOrder = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(ORDER_STORAGE_KEY));
      if (Array.isArray(saved)) {
        const known = saved.filter(k => WIDGET_KEYS.includes(k));
        WIDGET_KEYS.forEach((k) => { if (!known.includes(k)) known.push(k); });
        return known;
      }
    } catch (e) {
      // ignore malformed storage
    }
    return [...WIDGET_KEYS];
  };

  persist = (order) => {
    try {
      localStorage.setItem(ORDER_STORAGE_KEY, JSON.stringify(order));
    } catch (e) {
      // ignore storage failures
    }
  };

  onSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return;
    const { widgetsMetadata } = this.props;
    const present = this.state.order.filter(k => widgetsMetadata.object[k]);
    const notPresent = this.state.order.filter(k => !widgetsMetadata.object[k]);
    const next = [...arrayMove(present, oldIndex, newIndex), ...notPresent];
    this.setState({ order: next });
    this.persist(next);
  };

  render() {
    const {
      widgetsMetadata, chartFilters, title, hint
    } = this.props;
    if (widgetsMetadata.loading || _.isEmpty(widgetsMetadata.object)) return null;

    const present = this.state.order.filter(key => widgetsMetadata.object[key]);
    if (!present.length) return null;

    return (
      <Drawer>
        <Head>
          <Title>{title}</Title>
          <Hint>{hint}</Hint>
        </Head>
        <SortableRow
          axis="x"
          items={present}
          metadataByKey={widgetsMetadata.object}
          chartFilters={chartFilters}
          useDragHandle
          distance={4}
          helperClass="device-insights-card-helper"
          onSortEnd={this.onSortEnd}
        />
      </Drawer>
    );
  }
}

DevicesInsights.propTypes = {
  widgetsMetadata: PropTypes.shape({
    loading: PropTypes.bool,
    object: PropTypes.object
  }).isRequired,
  chartFilters: PropTypes.arrayOf(PropTypes.object),
  title: PropTypes.string,
  hint: PropTypes.string
};

DevicesInsights.defaultProps = {
  chartFilters: [],
  title: 'Insights',
  hint: 'Drag to rearrange'
};

export default DevicesInsights;
